import styled from 'styled-components';
import ESignDocumentTemplate from './ESignDocumentTemplate';
import MyColor from 'utils/MyColor';
import TextUtil from 'utils/TextUtil';
import {
  Label,
  LabeledReadOnlyInput,
  LabeledReadOnlyInputs,
  LabeledReadOnlyRadios,
  ReadOnlyRadios,
} from 'components/Fields';
import { useStoreList } from 'queries/useESignQueries';
import useBusinessNumber from '../hooks/useBusinessNumber';
import { usePDF } from '../eSignPDFContext';
import {
  CORPORATE_TYPES,
  DELIVERY_SCOPE_TYPE_LABELS,
  DELIVERY_SCOPE_TYPES,
  groupDeliveryAreas,
  hasBreakTime,
  hasBusinessHour,
  hasHoliday,
} from 'apis/types';
import BusinessHours from './StoreInfo/BusinessHours';
import BreakTimes from './StoreInfo/BreakTimes';
import Holidays from './StoreInfo/Holidays';

/* 주의사항 */
// 1. React.Fragment를 ESignDocumentTemplate 자식(자손 미포함) 요소로 사용하지 않습니다.
//    사용할 경우, 페이지 분할 로직이 정상 작동하지 않습니다.
// 2. ESignDocumentTemplate 자식 요소가 많아질수록, 페이지 분할 성능은 떨어집니다.
//    따라서, 가능한 적은 자식 요소를 사용합니다.

/* eslint react/jsx-key: "warn" */
export default function StoreInfo({ isMobile }) {
  const { businessNumber } = useBusinessNumber();
  const { data: storeList = [] } = useStoreList(businessNumber);
  const pdfContext = usePDF();

  const renderStoreDocuments = (model) => {
    return (
      <StoreWrapper>
        {/* 모든 매장의 문서 1은 같음 */}
        <div ref={pdfContext?.storeRefs.current[0]?.storeInfo1Ref}>{storeInfoDocument1(model)}</div>

        {/* 모든 매장이 각각 문서 2, 3을 가짐 */}
        {storeList.map((v, i) => (
          <>
            {storeInfoDocument2(v, i)}
            {storeInfoDocument3(v, i)}
          </>
        ))}

        {/* 모든 매장의 파일은 같음 */}
        <div ref={pdfContext?.storeRefs.current[0]?.fileRefs[0]}>{fileDocument1(model, 0)}</div>
        <div ref={pdfContext?.storeRefs.current[0]?.fileRefs[1]}>{fileDocument2(model)}</div>
      </StoreWrapper>
    );
  };

  const storeInfoDocument1 = (model) => (
    <ESignDocumentTemplate isMobile={isMobile}>
      <div style={{ paddingTop: 28 }}>
        <Title>꼬르륵 이용 신청 및 동의서</Title>
        <ColumnGap gap={42} />
        <FiledsWrap>
          {basicInfo(model)}
          {settlementInfo(model)}
          {corporateInfo(model)}
        </FiledsWrap>
      </div>
    </ESignDocumentTemplate>
  );

  /**
   * @param {import('apis/types').StoreModel} model
   * @param {number} index
   */
  const storeInfoDocument2 = (model, index) => {
    const isBusinessHourValid = model.businessHourList && hasBusinessHour(model.businessHourList);
    const isBreakTimeValid =
      model.storeBreakTimeList[0] && hasBreakTime(model.storeBreakTimeList[0]);
    const isHolidayValid = hasHoliday(model.storeHolidayList);

    return (
      <ESignDocumentTemplate
        isMobile={isMobile}
        shouldSplitPages={!isMobile}
        setDocumentRefs={(ref) => {
          pdfContext?.setStoreInfo2Refs(index, ref);
        }}
      >
        <ColumnGap gap={42} />
        <SubTitleWrapper>
          <span>매장</span>
          <IndexCircle>{index + 1}</IndexCircle>
          <span>정보</span>
        </SubTitleWrapper>
        {storeInfo(model)}
        <ColumnGap gap={38} />
        <SectionTitle>운영 정보</SectionTitle>
        {!isBusinessHourValid && !isBreakTimeValid && !isHolidayValid && (
          <Label $needFixedSize={false}>미설정</Label>
        )}
        {isBusinessHourValid && BusinessHours(model.businessHourList, isMobile)}
        {isHolidayValid && Holidays(model.storeHolidayList, isMobile)}
        <ColumnGap gap={20} />
        {isBreakTimeValid && BreakTimes(model.storeBreakTimeList[0], isMobile)}
      </ESignDocumentTemplate>
    );
  };

  /**
   * @param {import('apis/types').StoreModel} model
   */
  const storeInfoDocument3 = (model, index) => {
    const hasDeliveryArea =
      model.deliveryScopeType === DELIVERY_SCOPE_TYPES.RADIUS ||
      (model.deliveryScopeType === DELIVERY_SCOPE_TYPES.REGION &&
        model.storeDeliveryAreas.deliveryAreas.length > 0);

    return (
      <ESignDocumentTemplate
        isMobile={isMobile}
        shouldSplitPages={!isMobile}
        setDocumentRefs={(ref) => {
          pdfContext?.setStoreInfo3Refs(index, ref);
        }}
      >
        <ColumnGap gap={28} />
        <SectionTitle>배달 정보</SectionTitle>
        <ReadOnlyRadios
          style={{ marginBottom: 16 }}
          items={[
            {
              label: '배달',
              selected:
                model.storeOrderType.storeOrderType === 'DELIVERY' ||
                model.storeOrderType.storeOrderType === 'BOTH',
            },
            {
              label: '포장',
              selected:
                model.storeOrderType.storeOrderType === 'TAKEOUT' ||
                model.storeOrderType.storeOrderType === 'BOTH',
            },
          ]}
        />
        {model.storeOrderType.storeOrderType !== 'TAKEOUT' &&
          !model.deliveryCheck.freeDeliveryMode && [
            hasDeliveryArea && (
              <>
                {DeliveryArea(model)}
                <ColumnGap gap={20} />
              </>
            ),
            model.storeOrderAmountDeliveryFeeRule.orderAmountDeliveryFeeRules.length > 0 && (
              <>
                {OrderAmountDeliveryFeeRules(model)}
                <ColumnGap gap={20} />
              </>
            ),
            model.storeDeliveryAreaFeeRule.deliveryFeeRules.length > 0 && (
              <>
                {DeliveryFeeRule(model.storeDeliveryAreaFeeRule.deliveryFeeRules)}
                <ColumnGap gap={20} />
              </>
            ),
            model.storeDistanceDeliveryFeeRule.deliveryFeeRules.length > 0 &&
              StoreDistanceDeliveryFeeRule(model),
          ]}
      </ESignDocumentTemplate>
    );
  };

  const fileDocument1 = (model) => (
    <ESignDocumentTemplate isMobile={isMobile}>
      <div style={{ paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {fileWidget('별첨 파일 1 - 영업신고증', model.businessDeclarationCertificateUrl)}
        {fileWidget('별첨 파일 2 - 통장사본', model.bankFileUrl)}
      </div>
    </ESignDocumentTemplate>
  );

  const fileDocument2 = (model) => (
    <ESignDocumentTemplate isMobile={isMobile}>
      <div style={{ paddingTop: 12 }}>
        {fileWidget('별첨 파일 3 - 사업자등록증', model.businessRegistrationCertificateUrl)}
      </div>
    </ESignDocumentTemplate>
  );

  const fileWidget = (label, url) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <FileLabel>{label}</FileLabel>
      <FileWrapper>
        {url ? (
          <img src={url} style={{ maxWidth: '100%', maxHeight: '100%' }} />
        ) : (
          <Label $needFixedSize={false}>미설정</Label>
        )}
      </FileWrapper>
    </div>
  );

  /**
   * @param {import('apis/types').StoreModel} model
   */
  const basicInfo = (model) => {
    return (
      <div>
        <SubTitleWrapper>사업자 정보</SubTitleWrapper>
        <SectionTitle>기본 정보</SectionTitle>
        <FiledList>
          <LabeledReadOnlyInput
            label="상호명"
            value={model.businessRegistrationCertificateStoreName}
            isMobile={isMobile}
          />
          <LabeledReadOnlyInput
            label="대표자명"
            value={model.ownerName || '-'}
            isMobile={isMobile}
          />
          <LabeledReadOnlyInput
            label="사업자등록증 등록번호"
            value={model.businessRegistrationCertificateNumber}
            isMobile={isMobile}
          />
        </FiledList>
      </div>
    );
  };

  /**
   * @param {import('apis/types').StoreModel} model
   * @param {number | null} index
   */
  const storeInfo = (model) => {
    const mainCategoryTextList =
      model.mainCategory.length === 0
        ? ['-', '-']
        : model.mainCategory.length === 1
          ? [model.mainCategory[0], '-']
          : [model.mainCategory[0], model.mainCategory[1]];

    // const storeIngredientsArr = stringUtils.splitByLength(model.storeIngredients || '', 3000); // maxLength 더 크게 잡으면 pdf 추출 시 글자가 잘릴 수 있음

    return (
      <>
        {/* 페이지 분할 로직 최적화를 위해 div로 묶음 */}
        <div>
          <SectionTitle>매장</SectionTitle>
          <FiledList>
            <LabeledReadOnlyInput label="매장명" value={model.storeName} isMobile={isMobile} />
            <LabeledReadOnlyInput
              label="매장 전화번호"
              value={model.storeNumber || '-'}
              isMobile={isMobile}
            />
            <LabeledReadOnlyInputs
              label="매장 주소"
              values={[
                model.storeAddress?.roadAddress || model.storeAddress?.jibunAddress || '-',
                model.storeAddress?.detailAddress || '-',
              ]}
              isMobile={isMobile}
            />
            <LabeledReadOnlyInputs
              label="업종 카테고리"
              values={mainCategoryTextList}
              isMobile={isMobile}
            />
          </FiledList>
        </div>

        {/* 크롤링 데이터의 경우, 글자수가 많아서 pdf 영역을 넘칠 수 있음 */}
        {/* 넘치면 페이지가 분할될 수 있도록 ESignDocumentTemplate 자식으로 둠 */}
        {/* <LabeledReadOnlyInput
          style={{ marginTop: fieldGap }}
          label="가게소개"
          value={model.storeOwnerComment || '-'}
          isMobile={isMobile}
        />
        <LabeledReadOnlyInput
          style={{ marginTop: fieldGap }}
          label="사장님 공지"
          value={model.storeNotice || '-'}
          isMobile={isMobile}
        /> */}

        {/* {storeIngredientsArr.length < 2 ? (
          <LabeledReadOnlyInput
            style={{ marginTop: fieldGap }}
            label="원산지"
            value={storeIngredientsArr[0] || '-'}
            isMobile={isMobile}
          />
        ) : (
          LabeledReadOnlySplitedInput({
            style: { marginTop: fieldGap },
            label: '원산지',
            values: storeIngredientsArr,
            isMobile: isMobile,
          })
        )} */}
        <LabeledReadOnlyRadios
          isMobile={isMobile}
          style={{ marginTop: fieldGap }}
          label="주류 판매 여부"
          items={[
            {
              label: '사용',
              selected: model.alcohol,
            },
            {
              label: '미사용',
              selected: !model.alcohol,
            },
          ]}
        />
      </>
    );
  };

  const settlementInfo = (model) => {
    return (
      <div style={{ width: '100%' }}>
        <SectionTitle>정산정보</SectionTitle>
        <FiledList>
          <LabeledReadOnlyInput
            label="은행 타입"
            value={model.bankType || '-'}
            isMobile={isMobile}
          />
          <LabeledReadOnlyInput
            label="예금주"
            value={model.bankOwnerName || '-'}
            isMobile={isMobile}
          />
          <LabeledReadOnlyRadios
            isMobile={isMobile}
            label="사업자 유형"
            items={[
              {
                label: '개인/개인사업자',
                selected: !model.corporation,
              },
              {
                label: '법인사업자',
                selected: model.corporation,
              },
            ]}
          />
          <LabeledReadOnlyInput
            label="계좌 번호"
            value={model.bankNumber || '-'}
            isMobile={isMobile}
          />
        </FiledList>
      </div>
    );
  };

  /**
   * @param {import('apis/types').StoreModel} model
   */
  const corporateInfo = (model) => (
    <div>
      <SectionTitle>담당 운영사</SectionTitle>
      <LabeledReadOnlyInputs
        isMobile={isMobile}
        label="운영사 배정"
        values={[model.corporateName, CORPORATE_TYPES[model.corporateType]]}
      />
    </div>
  );

  return <>{renderStoreDocuments(storeList[0])}</>;
}

/// style
const fieldGap = 14;

const StoreWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;

  @media (max-width: 1000px) {
    padding-bottom: 23px;
    gap: 32px;
  }
`;

const Title = styled.div`
  font-size: 28px;
  font-weight: 600;
  text-align: center;
  color: ${MyColor.gray2C2C2C};
`;

const SubTitleWrapper = styled.div`
  margin: 0 0 20px;
  display: flex;
  align-items: center;
  gap: 4px;
  ${TextUtil.text24Bold}
  font-weight: 600;
  color: ${MyColor.gray90};
`;

const IndexCircle = styled.div`
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  background-color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
`;

const FiledsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 38px;
`;

const FiledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${fieldGap}px;
`;

const FileWrapper = styled.div`
  border: 1px dashed ${MyColor.gray20};
  border-radius: 8px;
  background-color: ${MyColor.gray0};
  width: 100%;
  height: 543px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FileLabel = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: ${MyColor.gray90};
`;

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: ${MyColor.gray90};
  margin-bottom: 20px;
`;

const TableTitle = styled.div`
  background-color: ${MyColor.gray5};
  border-top: 1px solid ${MyColor.gray10};
  border-bottom: 1px solid ${MyColor.gray10};
  padding: 8px 16px;
  ${TextUtil.text12w500}
  font-weight: 600;
  color: ${MyColor.gray50};
  display: flex;
`;

const DeliveryAreaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  border-bottom: 1px solid ${MyColor.gray10};
  padding: 4px 24px;
  ${TextUtil.text14Normal}
  font-weight: 500;
  color: ${MyColor.gray80};
`;

const TextRow = styled.div`
  display: flex;

  border-bottom: 1px solid ${MyColor.gray10};
  padding: 12px 0 12px 16px;
  ${TextUtil.text14Normal}
  font-weight: 500;
  color: ${MyColor.gray80};
`;

const Arrow = styled.img`
  width: 20px;
  aspect-ratio: 1/1;
`;

const StyledLastAddressName = styled.div`
  background-color: ${MyColor.gray5};
  border-radius: 6px;
  padding: 6px 12px;
  color: ${MyColor.gray90};
`;

const StyledTextAddressWrapper = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledStoreNumber = styled.div`
  color: ${MyColor.brand60};
  font-size: 32px;
  line-height: 1.6;
  font-weight: 700;
  margin-bottom: 8px;
`;

// TODO 파일 분리

const ColumnGap = ({ gap }) => <div style={{ height: gap }} />;
const ArrowIcon = () => <Arrow src="/icons/arrow_right_gray.png" />;

const DeliveryArea = (model) => {
  if (!model.deliveryScopeType || model.deliveryScopeType === DELIVERY_SCOPE_TYPES.NONE) {
    return [];
  }

  return [
    <Label key="DeliveryArea" style={{ marginBottom: 8 }} $needFixedSize={false}>
      배달 {DELIVERY_SCOPE_TYPE_LABELS[model.deliveryScopeType]}
    </Label>,

    model.deliveryScopeType == DELIVERY_SCOPE_TYPES.RADIUS ? (
      <div>{model.radiusKm}km</div>
    ) : (
      groupDeliveryAreas(model.storeDeliveryAreas.deliveryAreas).map((area, i) => {
        return (
          <div key={i} style={{ marginTop: i === 0 ? 0 : 4 }}>
            <TableTitle>지역</TableTitle>
            <DeliveryAreaRow>
              <StyledTextAddressWrapper>
                <div>{area.addressRegion1Name}</div>
                <ArrowIcon />
                <div>{area.addressRegion2Name}</div>
                {area.addressRegion4Name && (
                  <>
                    <ArrowIcon />
                    <div>{area.addressRegion3Name}</div>
                  </>
                )}
              </StyledTextAddressWrapper>
              <ArrowIcon />
              {area.addressRegion4Name
                ? area.addressRegion4Name.map((v, i) => (
                    <StyledLastAddressName key={i}>{v}</StyledLastAddressName>
                  ))
                : area.addressRegion3Name.map((v, i) => (
                    <StyledLastAddressName key={i}>{v}</StyledLastAddressName>
                  ))}
            </DeliveryAreaRow>
          </div>
        );
      })
    ),
  ];
};

const OrderAmountDeliveryFeeRules = (model) => {
  return [
    <Label key="orderAmountDeliveryFeeRules" style={{ marginBottom: 8 }} $needFixedSize={false}>
      주문 금액별 배달요금
    </Label>,
    <TableTitle key="orderAmountDeliveryFeeRules-table-head">
      <div style={{ width: 184 }}>주문 금액</div>
      배달비
    </TableTitle>,
    model.storeOrderAmountDeliveryFeeRule.orderAmountDeliveryFeeRules.map((fee, i) => (
      <TextRow key={i}>
        <div style={{ width: 184 }}>{fee.orderAmountLowerLimit.toLocaleString()}원 이상</div>
        {fee.extraDeliveryFee.toLocaleString()}원
      </TextRow>
    )),
  ];
};

// TODO 테스트
const DeliveryFeeRule = (deliveryFeeRules) => {
  if (deliveryFeeRules.length === 0) return null;

  return [
    <Label key={'DeliveryFeeRule'}>지역별 배달 요금</Label>,
    DeliveryFeeRules(deliveryFeeRules),
  ];
};

const DeliveryFeeRules = (deliveryFeeRules) => {
  return deliveryFeeRules.map((rule, i) => {
    const groupedAreas = groupDeliveryAreas(rule.deliveryAreas);

    return [
      <TableTitle key={`title-${i}`} style={{ marginTop: i === 0 ? 0 : 4 }}>
        지역
        <div style={{ marginLeft: 'auto' }}>
          추가 배달비 : +{rule.extraDeliveryFee.toLocaleString()}원
        </div>
      </TableTitle>,
      ...groupedAreas.map((area, j) => (
        <DeliveryAreaRow
          key={`area-${i}-${j}`}
          style={
            j === groupedAreas.length - 1
              ? null
              : {
                  border: 'none',
                }
          }
        >
          <StyledTextAddressWrapper>
            <div>{area.addressRegion1Name}</div>
            <ArrowIcon />
            <div>{area.addressRegion2Name}</div>
            {area.addressRegion4Name && (
              <>
                <ArrowIcon />
                <div>{area.addressRegion3Name}</div>
              </>
            )}
          </StyledTextAddressWrapper>
          <ArrowIcon />
          {(area.addressRegion4Name || area.addressRegion3Name).map((v, k) => (
            <StyledLastAddressName key={k}>{v}</StyledLastAddressName>
          ))}
        </DeliveryAreaRow>
      )),
    ];
  });
};

const StoreDistanceDeliveryFeeRule = (model) => {
  return [
    <Label key="deliveryFeeRules" style={{ marginBottom: 8 }} $needFixedSize={false}>
      거리별 추가 배달 요금
    </Label>,
    <TableTitle key="deliveryFeeRules-table-head">
      <div style={{ width: 160 }}>배달 총 거리</div>
      추가 배달비
    </TableTitle>,
    model.storeDistanceDeliveryFeeRule.deliveryFeeRules.map((fee, i) => (
      <TextRow key={i}>
        <div style={{ width: 160 }}>{fee.deliveryDistanceLowerLimit.toLocaleString()}km 이상</div>
        <div style={{ width: 136 }}>{fee.extraFeeDistanceUnit.toLocaleString()}m 당</div>+{' '}
        {fee.extraDeliveryFee.toLocaleString()}원
      </TextRow>
    )),
  ];
};
